import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { testId } from '../../../../utils/testIds';
import { UNSELECTED_OPTION_VALUE } from '../../constants';
import './styles.scss';

const norm = s => String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

function AsyncSearchSelect({
  config,
  value,
  onChange,
  onBlur,
  disabled,
  required,
  error,
  fetchOptions,
  placeholder,
  minChars = 0,
  debounceMs = 250,
  preloadedOptions = null,
  allowFreeText = false,
}) {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(preloadedOptions || []);

  useEffect(() => {
    if (!isOpen && Array.isArray(preloadedOptions)) {
      setOptions(preloadedOptions);
    }
  }, [preloadedOptions, isOpen]);
  const wrapRef = useRef(null);
  const wasOpen = useRef(false);
  const debounceTimer = useRef(null);
  const requestId = useRef(0);
  const justSelected = useRef(false);

  const selectedLabel = useMemo(() => {
    if (value === null || value === undefined || value === '') return '';
    const selected = options.find(o => String(o.value) === String(value));
    return selected?.label ?? String(value);
  }, [options, value]);

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      if (justSelected.current) {
        justSelected.current = false;
      } else if (allowFreeText) {
        const typed = searchText.trim();
        if (!typed) {
          if (value) onChange('');
          setSearchText('');
        } else {
          const matched = options.find(
            o => norm(o.label) === norm(typed) || norm(o.value) === norm(typed)
          );
          if (matched) {
            if (String(matched.value) !== String(value)) onChange(String(matched.value));
            setSearchText(matched.label);
          } else if (norm(typed) !== norm(selectedLabel)) {
            const clamped = config?.maxLength ? typed.slice(0, config.maxLength) : typed;
            onChange(clamped);
            setSearchText(clamped);
          }
        }
        onBlur?.();
      } else {
        setSearchText(selectedLabel);
      }
    }
    wasOpen.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedLabel, allowFreeText]);

  useEffect(() => {
    if (!isOpen || !fetchOptions) return;

    const query = searchText.trim();
    if (query.length < minChars) {
      setOptions([]);
      setLoading(false);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    setLoading(true);
    const currentRequestId = ++requestId.current;

    debounceTimer.current = setTimeout(async () => {
      try {
        const results = await fetchOptions(query);
        
        if (currentRequestId === requestId.current) {
          setOptions(Array.isArray(results) ? results : []);
          setLoading(false);
        }
      } catch (err) {
        console.error('[AsyncSearchSelect] fetchOptions error:', err);
        if (currentRequestId === requestId.current) {
          setOptions([]);
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchText, isOpen, fetchOptions, minChars, debounceMs]);

  useEffect(() => {
    if (!isOpen) return;
    
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  const handleFocus = useCallback(() => {
    if (isOpen || disabled) return;
    setSearchText(allowFreeText ? selectedLabel : '');
    setIsOpen(true);
  }, [isOpen, disabled, allowFreeText, selectedLabel]);

  const handleChange = useCallback((e) => {
    setSearchText(e.target.value);
    if (!isOpen) setIsOpen(true);
  }, [isOpen]);

  const handleSelect = useCallback((opt) => {
    justSelected.current = true;
    onChange(String(opt.value));
    setSearchText(opt.label);
    setIsOpen(false);
  }, [onChange]);

  const displayPlaceholder = placeholder ?? config?.fieldSettings?.placeholder ?? 'Search...';

  return (
    <div ref={wrapRef} className={`field-engine-async-search-select${disabled ? ' disabled' : ''}`}>
      <input
        data-test={testId('field', config?.fieldKey, 'input')}
        type="text"
        className={`field-engine-input${error ? ' field-engine-input--error' : ''}${disabled ? ' disabled' : ''}`}
        value={isOpen ? searchText : selectedLabel}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={() => {
          setTimeout(() => {
            if (!wrapRef.current?.contains(document.activeElement)) {
              setIsOpen(false);
            }
          }, 200);
        }}
        disabled={disabled}
        required={required}
        placeholder={displayPlaceholder}
        autoComplete="off"
        maxLength={config?.maxLength || undefined}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-required={required}
        aria-invalid={!!error}
      />
      {isOpen && (
        <ul className="field-engine-searchable-dropdown" role="listbox">
          {loading ? (
            <li className="field-engine-searchable-option field-engine-searchable-option--loading">
              Loading...
            </li>
          ) : options.length > 0 ? (
            options.map(opt => (
              <li
                key={String(opt.value)}
                role="option"
                aria-selected={String(opt.value) === String(value)}
                className={`field-engine-searchable-option${String(opt.value) === String(value) ? ' field-engine-searchable-option--selected' : ''}`}
                onMouseDown={e => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
              >
                {opt.label}
              </li>
            ))
          ) : (
            <li className="field-engine-searchable-option field-engine-searchable-option--empty">
              {searchText.length < minChars
                ? `Type at least ${minChars} character${minChars > 1 ? 's' : ''} to search`
                : 'No matches found'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default AsyncSearchSelect;
